import { useAppToast } from "@/hooks/useAppToast";
import { useMobileOtp } from "@/hooks/useMobileOtp";
import { catchErrorHandler } from "@picovico/utils";
import useLogout from "hooks/useLogout";
import { merge } from "lodash";
import { useState } from "react";
import { useDispatch } from "react-redux";
import { useAppSelector } from "store/hooks";
import {
  useUpdateUserProfileMutation,
  useVerifyMobileVerifiCationOtpMutation,
} from "store/services/user";
import { updateUserProfileAction } from "store/slices/userSlice";

import MobileVerificationModal, {
  MobileVerificationModalProps,
  MobileVerificationStep,
} from "./MobileVerificationModal";

export type MobileVerificationGateProps = Pick<
  MobileVerificationModalProps,
  | "strings"
  | "styleProps"
  | "otpLength"
  | "mobileValidationSchema"
  | "otpValidationSchema"
>;

// Mounted once, app-wide (see layout/AuthHandler). Blocks the rest of the
// site behind a non-dismissable modal for any logged-in user whose profile
// (`users/me`) has no `mobile` on record yet, until one is captured (and, with
// `proceed_to_otp_verification`, verified). `is_verified` doesn't open it. Opt-in
// per tenant via `layout_config.enable_mobile_verification_modal`; copy +
// styling come from `layout_config.mobile_verification_config`, with any
// props passed in directly taking precedence.
//
// Flow: submitting the mobile step PATCHes it onto the profile
// (user_profile_update) only when the value was actually edited from what's
// already on record — then, once that succeeds (or is skipped as a no-op),
// triggers the same membership OTP endpoints the profile page's "Mobile
// Verification" section uses (get/verify-mobile-verification-otp) to text +
// confirm a code for it.
//
// WhatsApp (whatsapp-messaging 02 §10, 04 §3): the code may go on WhatsApp.
// The OTP step follows the answer's `channel` ("We sent a code on WhatsApp
// to …"), and 20 s later, when `fallback_channels` has `sms`, offers "Send by
// SMS instead"; later resends stay on SMS. A 429 reads "Too many codes
// requested. Try again in N seconds". An older server (no `channel`) gets
// exactly the old flow (`useMobileOtp`).
function MobileVerificationGate(props: MobileVerificationGateProps) {
  const dispatch = useDispatch();
  const { error: errorToast } = useAppToast();
  const { isInitialized, isLoggedIn, user } = useAppSelector(
    (state) => state.user,
  );
  const {
    enable_mobile_verification_modal = false,
    proceed_to_otp_verification = true,
    mobile_verification_config,
  } = useAppSelector((state) => state.config.layout_config ?? {});

  const [step, setStep] = useState<MobileVerificationStep>("mobile");
  const [mobile, setMobile] = useState(() => user?.mobile ?? "");
  const [verificationId, setVerificationId] = useState<string | null>(null);

  const [updateUserProfile, { isLoading: isSubmittingMobile }] =
    useUpdateUserProfileMutation();
  const otpRequest = useMobileOtp();
  const [verifyOtp, { isLoading: isVerifyingOtp }] =
    useVerifyMobileVerifiCationOtpMutation();

  // Only for a logged-in user with no number on record, and only when the tenant
  // enabled it. `is_verified` plays no part. Once the code step is showing it
  // stays open even though the number is now on the profile, until the code is
  // verified (the OTP flow) — otherwise a profile refresh would close it midway.
  const isOpen = Boolean(
    enable_mobile_verification_modal &&
      isInitialized &&
      isLoggedIn &&
      (!user?.mobile || step === "otp"),
  );


  const sendOtp = async () => {
    const sent = await otpRequest.send();
    if (!sent) return;
    setVerificationId(sent.verificationId);
    setStep("otp");
  };

  const handleSendBySms = async () => {
    const sent = await otpRequest.sendBySms();
    if (sent) setVerificationId(sent.verificationId);
  };

  const handleSubmitMobile = async (value: string) => {
    setMobile(value);

    // OTP step switched off: just save the number. Once it is in the store the
    // modal closes (it only opens for a profile with no number). No code is
    // sent and `is_verified` is left alone.
    if (!proceed_to_otp_verification) {
      await updateUserProfile({ mobile: value })
        .unwrap()
        .then(() => {
          dispatch(updateUserProfileAction({ mobile: value }));
        })
        .catch((err) => catchErrorHandler(err, errorToast));
      return;
    }

    // OTP step on: the status changes only once the code is verified
    // (`handleVerifyOtp`).
    if (value === user?.mobile) {
      await sendOtp();
      return;
    }

    await updateUserProfile({ mobile: value })
      .unwrap()
      .then(sendOtp)
      .catch((err) => catchErrorHandler(err, errorToast));
  };

  const handleVerifyOtp = async (otp: string) => {
    await verifyOtp({ verification_id: verificationId, code: otp })
      .unwrap()
      .then(() => {
        dispatch(updateUserProfileAction({ mobile, is_verified: true }));
        setStep("mobile");
      })
      .catch((err) => catchErrorHandler(err, errorToast));
  };

  const handleResendOtp = async () => {
    await sendOtp();
  };

  const handleBack = () => {
    setVerificationId(null);
    otpRequest.reset();
    setStep("mobile");
  };

  const { logout: handleLogout } = useLogout();

  return (
    <MobileVerificationModal
      isOpen={isOpen}
      step={step}
      mobile={mobile}
      isSubmittingMobile={isSubmittingMobile}
      isVerifyingOtp={isVerifyingOtp}
      isResending={otpRequest.isSending}
      otpChannel={otpRequest.sent?.channel}
      showSmsFallback={otpRequest.smsOffered}
      onSendBySms={handleSendBySms}
      onSubmitMobile={handleSubmitMobile}
      onVerifyOtp={handleVerifyOtp}
      onResendOtp={handleResendOtp}
      onBack={handleBack}
      onLogout={handleLogout}
      otpLength={mobile_verification_config?.otp_length}
      {...props}
      strings={merge({}, mobile_verification_config?.strings, props.strings)}
      styleProps={merge(
        {},
        mobile_verification_config?.style_props,
        props.styleProps,
      )}
    />
  );
}

export default MobileVerificationGate;
